import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { brandingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Eye, EyeOff, Lock, Mail, AlertCircle } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [branding, setBranding] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch public branding (no auth required)
    brandingAPI.getPublic().then(res => setBranding(res.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid-bg flex">
      {/* Left side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="AEGIS Logo" className="h-12 w-auto" />
            ) : (
              <div className="w-12 h-12 rounded bg-primary/20 flex items-center justify-center neon-glow">
                <Shield className="w-7 h-7 text-primary" />
              </div>
            )}
            <div>
              <h1 className="font-secondary text-xl font-bold uppercase tracking-wider text-white">
                {branding?.header_text || 'AEGIS'}
              </h1>
              <p className="text-xs font-mono text-muted-foreground tracking-widest">
                FINANCIAL INTELLIGENCE
              </p>
            </div>
          </div>

          {/* Login card */}
          <div className="glass rounded-sm p-8">
            <div className="mb-6">
              <h2 className="font-secondary text-2xl font-bold uppercase tracking-wide text-white">
                Sign In
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Access AEGIS Financial Intelligence
              </p>
            </div>

            {error && (
              <div className="mb-6 p-3 bg-destructive/10 border border-destructive/30 rounded-sm flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="label-mono">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="agent@agency.gov"
                    className="pl-10 bg-black/20 border-white/10 focus:border-primary/50 rounded-sm font-mono text-sm"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="label-mono">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 pr-10 bg-black/20 border-white/10 focus:border-primary/50 rounded-sm"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-secondary uppercase tracking-wider rounded-sm h-11 neon-glow-sm"
                data-testid="login-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Authenticating...
                  </div>
                ) : (
                  'Access Platform'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="text-primary hover:underline"
                  data-testid="register-link"
                >
                  Register here
                </Link>
              </p>
            </div>
          </div>

          {/* Security notice */}
          <div className="mt-6 p-4 bg-card/30 border border-white/5 rounded-sm">
            <p className="text-[10px] font-mono text-muted-foreground/70 text-center uppercase tracking-wider">
              This is a secure critical system. Unauthorized access is prohibited
              and subject to prosecution under applicable laws.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Background image */}
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1644673640991-f3c4a9dafe9a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxnbG9iYWwlMjBkaWdpdGFsJTIwbmV0d29yayUyMG1hcCUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc2OTExNzg4MHww&ixlib=rb-4.1.0&q=85)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
        <div className="absolute bottom-8 left-8 right-8">
          <div className="glass p-6 rounded-sm">
            <h3 className="font-secondary text-lg font-bold uppercase tracking-wide text-primary mb-2">
              Aegis Financial Intelligence
            </h3>
            <p className="text-sm text-muted-foreground">
              Protecting the nation through advanced financial intelligence oversight.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
